import { useQuery } from '@tanstack/react-query';
import { Cable, CircleAlert, Plus, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { StatusBadge } from '../components/StatusBadge';
import { useAuth } from '../features/auth/AuthProvider';
import { integrationsKey, listIntegrations, type Integration } from '../features/integrations/api';
import { IntegrationDrawer } from '../features/integrations/IntegrationDrawer';
import { useTimeFormat } from '../lib/useTimeFormat';

type DrawerState = { integration?: Integration } | null;

const scope = (i: Integration) =>
  i.mode === 'all'
    ? 'Todas as câmeras'
    : [
        i.client_ids?.length ? `${String(i.client_ids.length)} cliente(s)` : '',
        i.site_ids.length ? `${String(i.site_ids.length)} conta(s)` : '',
        i.device_ids.length ? `${String(i.device_ids.length)} dispositivo(s)` : '',
      ]
        .filter(Boolean)
        .join(' + ') || 'Nenhuma câmera';

/** Systems that receive video from the Pro Guard (ADR-024). */
export function IntegrationsPage() {
  const { request, can } = useAuth();
  const fmt = useTimeFormat();
  const [drawer, setDrawer] = useState<DrawerState>(null);
  const list = useQuery({ queryKey: integrationsKey, queryFn: () => listIntegrations(request) });
  const rtspURL = list.data?.[0]?.rtsp_url ?? '';
  // The "Integração" profile governs every integration (ADR-027).
  const liveDenied =
    list.data?.[0] !== undefined && !list.data[0].permissions?.includes('live.view');

  return (
    <>
      <PageHeader
        title="Integrações"
        description="Sistemas que recebem vídeo do Pro Guard (ex.: Somaseg), cada um com usuário, senha e as câmeras liberadas."
        actions={
          <Button
            variant="primary"
            onClick={() => {
              setDrawer({});
            }}
          >
            <Plus aria-hidden="true" className="size-4" strokeWidth={2} />
            Nova integração
          </Button>
        }
      />
      <div className="space-y-3 p-4 sm:p-6">
        {liveDenied && (
          <p
            role="alert"
            className="flex items-start gap-2 rounded-md border border-warn/40 bg-warn/10 px-3 py-2 text-[13px] text-fg"
          >
            <TriangleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-warn" />
            <span>
              O perfil Integração não tem &ldquo;Assistir ao vivo&rdquo;: nenhuma integração recebe
              vídeo agora.{' '}
              {can('roles.read') && (
                <Link to="/roles" className="text-accent hover:underline">
                  Ajustar em Perfis e permissões
                </Link>
              )}
            </span>
          </p>
        )}
        <div className="overflow-hidden rounded-lg border border-line bg-surface">
          {list.isError ? (
            <EmptyState
              tone="crit"
              icon={CircleAlert}
              title="Não foi possível carregar as integrações"
              description="Verifique a conexão com o servidor e tente novamente."
              action={
                <Button variant="primary" onClick={() => void list.refetch()}>
                  Tentar novamente
                </Button>
              }
            />
          ) : list.data?.length === 0 ? (
            <EmptyState
              icon={Cable}
              title="Nenhuma integração"
              description="Crie uma integração para que outro sistema receba as câmeras por RTSP, como se o Pro Guard fosse um gravador Hikvision."
              action={
                <Button
                  variant="primary"
                  onClick={() => {
                    setDrawer({});
                  }}
                >
                  Criar a primeira integração
                </Button>
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table
                className="w-full min-w-[720px] text-left text-[13px]"
                aria-busy={list.isFetching}
              >
                <caption className="sr-only">Integrações da organização</caption>
                <thead className="text-2xs tracking-wide text-fg-subtle uppercase">
                  <tr className="border-b border-line">
                    <th scope="col" className="px-4 py-2 font-medium">
                      Sistema
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Usuário RTSP
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Câmeras
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Situação
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Último uso
                    </th>
                    <th scope="col" className="w-px px-4 py-2">
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!list.data
                    ? Array.from({ length: 3 }, (_, i) => (
                        <tr
                          key={i}
                          aria-hidden="true"
                          className="border-b border-line last:border-0"
                        >
                          {['w-40', 'w-28', 'w-32', 'w-16', 'w-24', 'w-10'].map((w) => (
                            <td key={w} className="px-4 py-3.5">
                              <div className={`h-3 animate-pulse rounded bg-surface-hover ${w}`} />
                            </td>
                          ))}
                        </tr>
                      ))
                    : list.data.map((i) => (
                        <tr
                          key={i.id}
                          className="border-b border-line last:border-0 hover:bg-surface-hover/50"
                        >
                          <td className="px-4 py-2.5 font-medium text-fg">{i.name}</td>
                          <td className="px-4 py-2.5 font-mono text-xs text-fg-muted">
                            {i.username}
                          </td>
                          <td className="px-4 py-2.5 text-fg-muted">{scope(i)}</td>
                          <td className="px-4 py-2.5">
                            <StatusBadge tone={i.status === 'active' ? 'ok' : 'neutral'}>
                              {i.status === 'active' ? 'Ativa' : 'Revogada'}
                            </StatusBadge>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-fg-muted">
                            {i.last_used_at ? fmt.relative(i.last_used_at) : 'Nunca'}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <Button
                              variant="ghost"
                              className="h-7"
                              aria-label={`Editar ${i.name}`}
                              onClick={() => {
                                setDrawer({ integration: i });
                              }}
                            >
                              Editar
                            </Button>
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {rtspURL && (
          <p className="text-xs text-fg-subtle">
            Endereço RTSP para os outros sistemas: <span className="font-mono">{rtspURL}</span>
          </p>
        )}
      </div>
      {drawer && (
        <IntegrationDrawer
          key={
            drawer.integration
              ? `${drawer.integration.id}:${drawer.integration.updated_at}:${drawer.integration.status}`
              : 'new'
          }
          {...(drawer.integration ? { integration: drawer.integration } : {})}
          onOpen={(i) => {
            setDrawer({ integration: i });
          }}
          rtspURL={rtspURL}
          onClose={() => {
            setDrawer(null);
          }}
        />
      )}
    </>
  );
}
